import * as React from "react";

/** A shorthand type-alias to write less fugly TS syntax */
export type Fun<a,b> = (_:a) => b

/** An action is an updater over an object `a`.
e.g.

```javascript
const increaseAge : Action<Person> = (p: Person) =>
   ({...p, age: p.age+1})

const setAge : Fun<number,Action<Person>> = (newAge) => (p) => ({...p, age:newAge})
```
*/
export type Action<a> = Fun<a,a>

/** The terminal type of Typescript. A type synonym for the type of the **empty object** */
export type Unit = {}

/** Is the reverse function composition operator. */
export const then = <a,b,c>(f:Fun<a,b>, g:Fun<b,c>) : Fun<a,c> => (x:a) => g(f(x))

/** @todo refactor to array syntax: 
```
type Pair<a,b> = [a,b]
export const fst = <a,b>(p : P<a,b>) : a => p[0]
export const snd = <a,b>(p : P<a,b>) : b => p[1]
```
*/
export type Pair<a,b> = { fst:a, snd:b }

/** @deprecated Will move to the array syntax directly. */
export const mk_pair = <a,b>(a:a, b:b) => ({ fst:a, snd:b })

/** the sum of two types, which is the discriminated union and not just the union of those types. 
 * @todo (Breaking change): move to the shorter definition (with one less .v access):
 * ```typescript
 * export type Sum_<l,r> = {kind: "l", v : l} | {kind: "r", v: r} 
 * ```
*/
export interface Sum<l,r> { v: { kind:"l", v:l } | { kind:"r", v:r } }

/** is the left injection (constructor) of the Sum */
export const inl = <l,r>(l:l) : Sum<l,r> => ({ v: { kind:"l", v:l } })

/** is the right injection (constructor) of the Sum */
export const inr = <l,r>(r:r) : Sum<l,r> => ({ v: { kind:"r", v:r } })

/** A way to deconstruct a [[Sum]] to any value of type `c`. 
 * @param onL the function to apply in case the Sum is left
 * @param onR the function to apply in case the Sum is right
*/
export const deconstruct = <l,r,c>(onL : Fun<l,c>, onR: Fun<r,c>, v: Sum<l,r>): c => 
  v.v.kind == "l" ? onL(v.v.v) : onR(v.v.v)

/**
 * This datatype signals a "box" that *may* or *may not* contain a value of type a.
 * If its kind is `l`, then the box is empty.
 * If its kind is `r`, the box has a value of type `a`.
 * @todo Since we have the built-in `undefined` and TS can be strict on it, we can perhaps move on it instead of `Option`?
 */
export type Option<a> = Sum<Unit,a>

/** The empty "box". 
 * Left constructor of Option. */
export const none = function<a>() : Option<a> { return inl<Unit,a>({}) }

/** Creates a "box" and puts inside a value `v` of type `a`.
 * Right constructor of Option. */
export const some = function<a>(v:a) : Option<a> { return inr<Unit,a>(v) }

/** Applies a function over an [[Option]] "box".
 * If the original box is empty, the empty box is returned.
 * If the original box is full, its original contents are altered by `f` and placed into a new box, which is returned.
 * @param f The function to transform the value inside the box
 * @param o The original box
 */
export const mapO = <a,b>(f : Fun<a,b>, o: Option<a>) : Option<b> =>
    o.v.kind == "r" ? some(f(o.v.v)) : none()

/**
 * Run a command `f` passing the contents of the [[Option]] box.
 * In case the box is empty, the `onSome` call does nothing (no-op).
 */
export const onSome : <a>(f : Fun<a,void>, o: Option<a>) => void = mapO 

/** Tears up the outer [[Option]] box and returns its contents.
 * @param def In case the box is empty, this **default** value is returned instead.
 * @param o The box to tear up.
 */
export const fromOption = function<a>(def: a, o : Option<a>) : a {
  return o.v.kind == "r" ? o.v.v : def
}

/** Common HTML Options of widgets
    All of them are optional.
*/
export type Options = Partial<{
  /** @deprecated */
  readonly:boolean
  /** @deprecated */
  autofocus:boolean
  /** Must be unique across the React application. React requires it. */
  key:string
  /** @deprecated */
  className:string
  /** @deprecated */
  role:string
  /** @deprecated */
  style:React.CSSProperties
  /** @deprecated */
  id:string
  /** @deprecated */
  extraProperties:{}
}>

/** Does two undefined checks, both on the existence of the object and the existence of 
 * the property of the object
 * @deprecated for internal-use only, will be unexported later
 */
export const option = <t extends Options, k extends keyof t>(k:k, o?:t) : t[k] | undefined =>
    o ? o[k] : undefined

/** The basic structure of a widget, which contains a callback function
 * and which is renderable.
 * 
 * It is the interface to be implemented when creating a custom widget using [[mkWidget]].
 */
export interface WidgetBase<o> { run:(on_done:(output:o) => void) => JSX.Element }

/** Is an extension of WidgetBase, plus a series of very important methods,
 * that simplify working/combining callbacks.
 */
export interface Widget<o> extends WidgetBase<o> {
  /** Wraps the `JSX.Element` of the internal widget in some extra markup.
  * This is a visual decorator only.
  * This has no impact on the data flow.
  * 
  * e.g.
  * ```javascript
  * w.wrapHTML(w => <div>{w}</div>)
  * ```
  * *
  * The given `JSX.Element` *must* appear in the returned value, otherwise
  * the widget will, effectively, stop working and producing data.
  * 
  * e.g. that will cause a lot of issues and damage because we have killed the
  * widget in a misguided attempt to make it pretty
  * ```javascript
  * w.wrapHTML(w => <div></div>)
  * ```
  */
  wrapHTML:(f:Action<JSX.Element>) => Widget<o>,

  /** Maps the data output value of the Widget, 
   * @param f The function has only access to the output result `o2` and based on its value can replace with a new result `o2`.
   * The function `f` however cannot alter the Widget/control/visual flow and replace it with a new widget or nothing inside there.
  */
  map:<o2>(f:Fun<o,o2>) => Widget<o2>,

  /** After the callee widget `Widget<o>` `render`s and `run`s, its output value will be tested by the function `p`.
   * If this test of `p(o)` fails, the widget will not propagate its data flow to the next widget, but instead will act as a `never` widget.
   * There is a big difference compared to [[onlyIf]], because upon a failed test, the inner widget does not even render/run: i.e. a `nothing` widget.
   */
  filter:(p:Fun<o,boolean>) => Widget<o>,
  
  /** @deprecated */
  then:<o2>(k:Fun<o,Widget<o2>>) => Widget<o2>,
  
  /** Forces a widget to fake any arbitrary output, by **never** returning it. */
  never:<o2>() => Widget<o2>,
}

/** Creates a widget from a cont -> JSX.Element */
export const fromJSX = <s, _>(w: (cont:(_:s) => void) => JSX.Element) : Widget<s> =>
  mkWidget({run:w})

/** @deprecated User [[fromJSX]]
 *  Is the smart constructor which adds all the utility methods on top of a [[WidgetBase]] in order to create a [[Widget]]. */
export const mkWidget = function<o>(w:WidgetBase<o>) : Widget<o> {
  return mk_widget(w)
}

/** @deprecated User [[wrapHTML]] instead. */
export const jsxToWidget = function<o>(jsx:JSX.Element) : Widget<o> {
  return mkWidget({run: _ => jsx})
}

/** @deprecated Use [[mkWidget]] instead. */
export const mk_widget = <w extends WidgetBase<o>,o>(w:w) : Widget<o> =>
   ({
    run:w.run,
    wrapHTML:function(f:Action<JSX.Element>) { return wrapHTML<o>(f)(this) },
    map:function<o2>(f:Fun<o,o2>) { return map(f)(this) },
    filter:function(p:Fun<o,boolean>) { return filter(p)(this) },
    then:function<o2>(k:Fun<o,Widget<o2>>) { return bind(this, k) },
    never:function<o2>() { return never(this) }
  })

/** A widget that depends on some input. */
export type IOWidget<i,o> = (input:i) => Widget<o>

/** This is the empty widget with no visuals and with no data flow. */
export const nothing = function<o>() : Widget<o> {
    // Note: `nothing()` cannot become a polymorphic constant, because TS does not support that (yet).
  return mk_widget({
    run:(on_done_transform:(output:o) => void) : JSX.Element => {
      return null!
    }
  }) }

/** internal only */
const never_io = <i,o1,o2>(inner_widget:IOWidget<i,o1>) : IOWidget<i,o2> =>
  (input:i) => mk_widget({
    run:(on_done_transform:(output:o2) => void) =>
      inner_widget(input).run(_ => {})
  })

/** internal only */
const never = function<o>(inner_widget:Widget<{}>) : Widget<o> {
  return never_io<Unit,{},o>(_ => inner_widget)({})
}

/** internal only */
const wrapHTML = function<a>(wrap:Action<JSX.Element>) : Action<Widget<a>> {
  return widget_a => mk_widget({
    ...widget_a,
    run:(on_done:(output:a) => void) =>
      wrap(widget_a.run(res_a => on_done(res_a))) }) }

/** @todo just implementation, should not be exported, internal only */
export const map = <a,b>(f:Fun<a,b>) : Fun<Widget<a>, Widget<b>> => widget_a => 
  mk_widget({
    ...widget_a,
    run:(on_done_b:(output_b:b) => void) =>
      widget_a.run(res_a => on_done_b(f(res_a))) })

/** internal only */
interface JoinerProps<a> {
  widget_a2:Widget<Widget<a>>
  on_done_a:(output_a:a) => void
}
/** internal only */
interface JoinerState<a> {
  inner_widget:Widget<a> | undefined
}
/** internal only */
class Joiner<a> extends React.Component<JoinerProps<a>, JoinerState<a>> {
  constructor(props:JoinerProps<a>, context:any) {
    super(props, context)

    this.state = { inner_widget:undefined }
  }

  render() {
    return <>
      {this.props.widget_a2.run(widget_a => this.setState(s => ({...s, inner_widget:widget_a})))}
      {this.state.inner_widget ?
        this.state.inner_widget.run(this.props.on_done_a)
      : null}
    </>
  }
}

/** internal only */
const join = function<a>(widget_a2:Widget<Widget<a>>) : Widget<a> {
  return mk_widget({
    run:(on_done_a:(output_a:a) => void) =>
      React.createElement<JoinerProps<a>>(Joiner, {widget_a2, on_done_a}) })
}

/** @todo just implementation, should not be exported, internal only */
export const bind = <a,b>(widget_a:Widget<a>, widget_b_from_a:Fun<a,Widget<b>>) : Widget<b> =>
     join(map(widget_b_from_a)(widget_a))

/** @todo just implementation, should not be exported, internal only */
export const filter = function<a>(p:Fun<a,boolean>) : Action<Widget<a>> {
  return inner_widget => ({
    ...inner_widget,
    run:(on_done:(output:a) => void) =>
      inner_widget.run(res_a => p(res_a) ? on_done(res_a) : null) }) }


